import api from './axiosConfig';
import AuthService from 'services/auth.service';
import { EventSourcePolyfill } from 'event-source-polyfill';
import { getBackendUrl } from '../config';

export interface CampaignFile {
  name: string;
  size: number;
  modifiedDate: string;
  isDirectory: boolean;
}

export interface Campaign {
  id: number;
  name: string;
  scriptAvailable: boolean;
  scriptName: string;
  files: CampaignFile[];
  isRunning: boolean;
}

export interface ScriptOutput {
  type: 'output' | 'error' | 'exit';
  content: string;
  timestamp: string;
  campaign: string;
  campaignId: number;
}

export const fetchCampaigns = async (): Promise<Campaign[]> => {
  try {
    const response = await api.get('calllistavailablefiles');
    return response.data.map((campaign: Campaign) => ({
      ...campaign,
      files: campaign.files.map(file => ({
        ...file,
        modifiedDate: new Date(file.modifiedDate).toLocaleDateString('en-GB', {
          day: '2-digit',
          month: 'short',
          year: 'numeric'
        })
      }))
    }));
  } catch (error) {
    console.error('Error fetching campaigns:', error);
    throw error;
  }
};

export const continuePreviousCallingList = async (
  campaignId: number
): Promise<{ success: boolean; message: string }> => {
  try {
    const response = await api.post(`calllistcontinue/${campaignId}`);
    return response.data;
  } catch (error) {
    console.error('Error continuing with previous file:', error);
    throw error;
  }
};

export const executeScript = (
  campaignId: number,
  onOutput: (output: ScriptOutput) => void,
  onError: (error: Error) => void,
  onComplete: () => void,
  archiveUploads: boolean = true
): (() => void) => {
  const currentUser = AuthService.getCurrentUser();

  if (!currentUser?.accessToken) {
    onError(new Error('No authentication token available'));
    return () => {};
  }

  const url = `${getBackendUrl()}calllistuploading/${campaignId}/execute?archive=${archiveUploads}`;

  const eventSource = new EventSourcePolyfill(url, {
    headers: {
      Authorization: `Bearer ${currentUser.accessToken}`
    },
    heartbeatTimeout: 60000,
    withCredentials: true
  });

  eventSource.onmessage = event => {
    try {
      const data: ScriptOutput = JSON.parse(event.data);
      onOutput(data);

      if (data.type === 'exit') {
        eventSource.close();
        onComplete();
      }
    } catch (error) {
      console.error('Error parsing SSE message:', error);
      onError(new Error('Error processing server response'));
    }
  };

  eventSource.onerror = event => {
    console.error('EventSource error:', event);
    eventSource.close();
    onError(new Error('Connection error with server'));
  };

  return () => {
    eventSource.close();
  };
};

export const getCampaignStatus = async (
  campaignId: number
): Promise<boolean> => {
  try {
    const response = await api.get(`calllistuploading/${campaignId}/status`);
    return response.data.isRunning;
  } catch (error) {
    console.error('Error checking campaign status:', error);
    throw error;
  }
};
